import React, { useMemo, useState } from "react"

import { OMReference } from "@apptivity-lab/firmament-node-sdk"
import { useMachine, useRestoredUniverse, startOfMonth, endOfMonth, useProfile, startOfDay } from "@lib"

import { CnxBooking, CnxMeeting, CnxMeetingTemplate } from "@firmament-packages/connectx"
import { AffAttendeeProfile } from "@firmament-packages/aff"
import "../../../firmament-packages/aff/extensions/CatCategoryExtensions"

import { CnxMeetingListStateMachine } from "@machines/CnxMeetingListStateMachine"

import ActivityIndicator from "@components/ActivityIndicator"
import { Segment, SegmentedControl } from "@components/SegmentedControl"
import { CnxMeetingCell } from "@components/domain/CnxMeeting"

const UpcomingMeetingList: React.FunctionComponent<{
    meetingTemplate?: OMReference<CnxMeetingTemplate>
    meeting?: CnxMeeting,
    segmentIndex?: number,
    onBookButtonClick?: (meeting: CnxMeeting) => void,
    onCancelButtonClick?: (meeting: CnxMeeting, booking: CnxBooking) => void,
    onEnterButtonClick?: (meeting: CnxMeeting) => void
}> = ({
    meetingTemplate,
    meeting,
    segmentIndex,
    onBookButtonClick,
    onCancelButtonClick,
    onEnterButtonClick
}) => {
    const attendeeProfile = useProfile(AffAttendeeProfile)

    const [machine, machineState] = useMachine(CnxMeetingListStateMachine)

    useRestoredUniverse(useMemo(() => () => {
        if (meetingTemplate) {
            // Show upcoming meetings up to end of next month from current day
            machine.refresh({
                segment: "ALL",
                fromDate: startOfMonth(new Date(), 0, attendeeProfile?.timezone || undefined),
                toDate: endOfMonth(new Date(), 1),
                template: meetingTemplate
            })
        } else if (meeting) {
            machine.refresh({
                segment: "ALL",
                id: new OMReference(CnxMeeting, meeting.id)
            })
        }
    }, [machine, meetingTemplate, meeting, attendeeProfile?.timezone]))

    const [monthSegment, setMonthSegment] = useState<number>(segmentIndex || 0)

    const currentMonthTitle = useMemo(() => {
        const today = startOfDay(new Date(), attendeeProfile?.timezone || undefined)
        return today.toLocaleDateString("en-US", { month: "long" })
    }, [attendeeProfile?.timezone])

    switch (machineState.name) {
        case "loading":
            return (
                <>
                    <div className="px-[20px] text-title2 text-black-900">Sessions ini {currentMonthTitle}</div>
                    <div className="px-[20px] text-subhead text-black-700">Timezone: {attendeeProfile?.timezone || new Intl.DateTimeFormat("en").resolvedOptions().timeZone}</div>

                    <div className="p-[40px]">
                        <ActivityIndicator className="mx-auto" />
                    </div>
                </>
            )

        case "presenting":
            const meetingsByMonth = machineState.meetings.reduce((grouped: Record<string, CnxMeeting[]>, current: CnxMeeting) => {
                const meetingMonth = current.localYearMonth(attendeeProfile?.timezone || undefined)
                if (!meetingMonth) {
                    return grouped
                }

                if (!grouped[meetingMonth]) {
                    grouped[meetingMonth] = []
                }
                grouped[meetingMonth].push(current)
                return grouped
            }, {})

            const groupedMeetings: CnxMeeting[][] = []
            Object.keys(meetingsByMonth).sort().forEach((each: string) => {
                groupedMeetings.push(meetingsByMonth[each])
            })

            return (
                <>
                    {Object.keys(meetingsByMonth).length > 1 &&
                        <div className="mb-[24px] px-[20px]">
                            <SegmentedControl value={monthSegment} onChange={(value) => setMonthSegment(value as number)}>
                                {groupedMeetings.map((eachGroup: CnxMeeting[], index: number) => {
                                    const scheduledMonth = eachGroup[eachGroup.length - 1].scheduledStartAt!
                                    const monthTitle = scheduledMonth.toLocaleDateString("en-US", { month: "long", timeZone: attendeeProfile?.timezone || undefined })
                                    return <Segment title={monthTitle} value={index} key={index} />
                                })}
                            </SegmentedControl>
                        </div>
                    }

                    <div className="px-[20px] text-title2 text-black-900">Sessions in {groupedMeetings[monthSegment || 0][0].scheduledStartAt!.toLocaleDateString("en-US", { month: "long", timeZone: attendeeProfile?.timezone || undefined })}</div>
                    <div className="px-[20px] text-subhead text-black-700">Timezone: {attendeeProfile?.timezone || new Intl.DateTimeFormat("en").resolvedOptions().timeZone}</div>

                    <div className="mt-[16px]">
                        {
                            (groupedMeetings[monthSegment || 0]).map((meeting: CnxMeeting) => {
                                return (
                                    <div key={meeting.id}>
                                        <CnxMeetingCell
                                            variant="SMALL_LIST"
                                            meeting={meeting}
                                            onBookButtonClick={onBookButtonClick}
                                            onCancelButtonClick={onCancelButtonClick}
                                            onEnterButtonClick={onEnterButtonClick}
                                            />
                                    </div>
                                )
                            })
                        }
                    </div>
                </>
            )

        case "showingError":
            return null

        case "showingDetail":
            return null

        default:
            return null
    }
}

export default UpcomingMeetingList
